import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Footer() {
  const [emailStatus, setEmailStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [emailError, setEmailError] = useState('');

  const handleNewsletterSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setEmailStatus('loading');
    setEmailError('');

    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = (formData.get('company_alt') as string || '').trim();
    if (honeypot) {
      setEmailStatus('success');
      form.reset();
      return;
    }

    try {
      const response = await fetch('http://zbpost.cn/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setEmailStatus('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || '订阅失败，请稍后重试';
        setEmailStatus('error');
        setEmailError(serverMsg.includes('spam') ? '订阅失败，请稍后重试' : serverMsg);
      }
    } catch {
      setEmailStatus('error');
      setEmailError('网络错误，请检查网络连接后重试');
    }
  };

  return (
    <footer className="bg-foreground-950 text-background-50">
      <div className="w-full px-4 md:px-6 py-14">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10">
          <div>
            <h3 className="font-heading text-2xl font-bold mb-4">韩漫网站</h3>
            <p className="text-sm text-foreground-300 leading-relaxed">
              韩漫在线观看首选平台。免费下拉式阅读，智能预加载，极速流畅体验。看韩漫就上韩漫网站。
            </p>
            <div className="flex gap-3 mt-5">
              <a href="http://zbpost.cn/" target="_blank" rel="nofollow noopener" className="w-9 h-9 flex items-center justify-center rounded-full bg-foreground-800 hover:bg-primary-500 transition-colors cursor-pointer" aria-label="微博">
                <i className="ri-links-line text-sm"></i>
              </a>
              <a href="http://zbpost.cn/" target="_blank" rel="nofollow noopener" className="w-9 h-9 flex items-center justify-center rounded-full bg-foreground-800 hover:bg-primary-500 transition-colors cursor-pointer" aria-label="微信">
                <i className="ri-wechat-line text-sm"></i>
              </a>
              <a href="http://zbpost.cn/" target="_blank" rel="nofollow noopener" className="w-9 h-9 flex items-center justify-center rounded-full bg-foreground-800 hover:bg-primary-500 transition-colors cursor-pointer" aria-label="哔哩哔哩">
                <i className="ri-movie-line text-sm"></i>
              </a>
            </div>
          </div>

          <div>
            <h4 className="font-semibold text-sm mb-4">漫画分类</h4>
            <ul className="space-y-2.5">
              {categories.map((cat) => (
                <li key={cat.id}>
                  <Link to={`/category/${cat.slug}`} className="text-sm text-foreground-300 hover:text-primary-400 transition-colors cursor-pointer">
                    {cat.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-semibold text-sm mb-4">快速链接</h4>
            <ul className="space-y-2.5">
              <li><Link to="/" className="text-sm text-foreground-300 hover:text-primary-400 transition-colors cursor-pointer">首页</Link></li>
              <li><Link to="/about" className="text-sm text-foreground-300 hover:text-primary-400 transition-colors cursor-pointer">关于我们</Link></li>
              <li><Link to="/" className="text-sm text-foreground-300 hover:text-primary-400 transition-colors cursor-pointer">热门推荐</Link></li>
              <li><Link to="/" className="text-sm text-foreground-300 hover:text-primary-400 transition-colors cursor-pointer">最新更新</Link></li>
              <li><Link to="/about" className="text-sm text-foreground-300 hover:text-primary-400 transition-colors cursor-pointer">联系我们</Link></li>
            </ul>
          </div>

          <div>
            <h4 className="font-semibold text-sm mb-4">订阅更新</h4>
            <p className="text-sm text-foreground-300 mb-3">第一时间获取最新韩漫更新通知</p>
            <form className="flex" onSubmit={handleNewsletterSubmit}>
              <input
                type="text"
                name="company_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
              />
              <input
                type="email"
                name="email"
                placeholder="输入邮箱地址"
                className="flex-1 px-4 py-2.5 rounded-l-full bg-foreground-800 text-sm text-background-50 placeholder:text-foreground-400 border border-foreground-700 focus:outline-none focus:border-primary-500"
                required
              />
              <button
                type="submit"
                disabled={emailStatus === 'loading'}
                className="px-4 py-2.5 bg-primary-500 text-white rounded-r-full hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60"
              >
                <i className="ri-send-plane-fill text-sm"></i>
              </button>
            </form>
            {emailStatus === 'success' && (
              <p className="text-xs text-accent-400 mt-2">订阅成功！感谢关注。</p>
            )}
            {emailStatus === 'error' && emailError && (
              <p className="text-xs text-primary-400 mt-2">{emailError}</p>
            )}
          </div>
        </div>

        <div className="border-t border-foreground-800 mt-10 pt-6 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-xs text-foreground-400">
            &copy; 2026 韩漫网站. 本站所有漫画版权归原作者所有，仅供学习交流使用。
          </p>
          <div className="flex gap-4">
            <a href="#" className="text-xs text-foreground-400 hover:text-foreground-200 cursor-pointer">隐私政策</a>
            <a href="#" className="text-xs text-foreground-400 hover:text-foreground-200 cursor-pointer">服务条款</a>
          </div>
        </div>
      </div>

      <div className="text-center pb-6 select-none">
        <span className="font-heading text-7xl font-black text-foreground-800 opacity-20">韩漫网站</span>
      </div>
    </footer>
  );
}